import { matchText } from '@/todos/fuzzy'

// One step of a hierarchy: what an item is called on this level (a list's owner, its account, ...).
export interface DrillLevel<T> {
  label: (item: T) => string
}

export interface DrillGroup {
  value: string
  count: number
}

// What to show for a path: the groups of the next level, or the items themselves once no level is left to choose.
export type DrillStep<T> =
  | { kind: 'groups'; level: number; groups: DrillGroup[] }
  | { kind: 'items'; items: T[] }

// An item matches a search when one of its texts matches (prefix, anywhere or the letters in order, like the tag search).
export function filterBySearch<T>(items: readonly T[], search: string, texts: (item: T) => string[]): T[] {
  const needle = search.trim().toLowerCase()
  if (!needle) return [...items]
  return items.filter((item) => texts(item).some((text) => matchText(text, needle) !== null))
}

function groupsOf<T>(items: readonly T[], level: DrillLevel<T>): DrillGroup[] {
  const counts = new Map<string, number>()
  for (const item of items) {
    const value = level.label(item)
    counts.set(value, (counts.get(value) ?? 0) + 1)
  }
  return [...counts].map(([value, count]) => ({ value, count }))
}

// Walks `path` down the levels (the values chosen so far). A level with a single value is skipped, so nobody has to tap
// through a list that has only one owner or one account. `level` is the index of the level the groups belong to.
export function drillDown<T>(items: readonly T[], levels: readonly DrillLevel<T>[], path: readonly string[]): DrillStep<T> {
  let rest = [...items]
  let taken = 0 // (path holds only the values of levels that were shown, a skipped level has none)
  for (let depth = 0; depth < levels.length; depth += 1) {
    const level = levels[depth]!
    const groups = groupsOf(rest, level)
    if (groups.length <= 1) continue // (zero or one group: nothing to choose)
    const chosen = path[taken]
    if (chosen === undefined) return { kind: 'groups', level: depth, groups }
    rest = rest.filter((item) => level.label(item) === chosen)
    taken += 1
  }
  return { kind: 'items', items: rest }
}

// The indexes of the levels `path` chose a value for (a skipped level is not one of them), so a card can leave out the
// fields that the breadcrumb already shows.
export function chosenLevels<T>(items: readonly T[], levels: readonly DrillLevel<T>[], path: readonly string[]): number[] {
  let rest = [...items]
  const chosen: number[] = []
  for (let depth = 0; depth < levels.length; depth += 1) {
    const level = levels[depth]!
    const value = path[chosen.length]
    if (value !== undefined && groupsOf(rest, level).length > 1) {
      rest = rest.filter((item) => level.label(item) === value)
      chosen.push(depth)
    }
  }
  return chosen
}
